import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Local constant for device fingerprint storage key
export const DEVICE_FINGERPRINT_KEY = 'device_fingerprint_key';

/**
 * Device Fingerprint Service
 *
 * Uses FingerprintJS to collect hardware-level characteristics
 * that remain consistent across different browsers on the same device.
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceFingerprintService {
  private fpPromise: Promise<any>;
  private cachedData: any = null;
  private cachedFingerprint: any = null;

  constructor() {
    // Initialize FingerprintJS once when service is created
    this.fpPromise = FingerprintJS.load();
    // Load cached fingerprint from localStorage on service initialization
    this.loadCachedFingerprint();
  }

  /**
   * Load cached fingerprint from localStorage
   */
  private loadCachedFingerprint(): void {
    try {
      const stored = localStorage.getItem(DEVICE_FINGERPRINT_KEY);
      if (stored) {
        this.cachedFingerprint = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading cached fingerprint:', error);
    }
  }

  /**
   * Save fingerprint to localStorage
   */
  private saveFingerprintToStorage(fingerprint: any): void {
    try {
      localStorage.setItem(DEVICE_FINGERPRINT_KEY, JSON.stringify(fingerprint));
    } catch (error) {
      console.error('Error saving fingerprint to localStorage:', error);
    }
  }

  /**
   * Collect comprehensive hardware fingerprint data
   * Returns data in format compatible with your backend API
   *
   * @returns Promise<any> Hardware characteristics object
   */
  async collectHardwareData(): Promise<any> {
    // Return cached data if available (same browser session)
    if (this.cachedData) {
      return this.cachedData;
    }

    try {
      const fp = await this.fpPromise;
      const result = await fp.get();

      // Map FingerprintJS components to your backend format
      const components = result.components;

      const hardwareData = {
        // Screen characteristics (very stable across browsers)
        screen_width: components.screenResolution?.value?.[0] || window.screen.width,
        screen_height: components.screenResolution?.value?.[1] || window.screen.height,
        screen_avail_width: window.screen.availWidth,
        screen_avail_height: window.screen.availHeight,
        screen_color_depth: components.colorDepth?.value || window.screen.colorDepth,
        screen_pixel_depth: window.screen.pixelDepth,
        screen_pixel_ratio: window.devicePixelRatio || 1,

        // Timezone (stable per location)
        timezone: components.timezone?.value || Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezone_offset: new Date().getTimezoneOffset(),

        // Hardware specifications (very stable)
        hardware_concurrency: components.hardwareConcurrency?.value || navigator.hardwareConcurrency,
        device_memory: components.deviceMemory?.value || (navigator as any).deviceMemory,

        // Platform/OS
        platform: components.platform?.value || navigator.platform,

        // Touch support (for mobile/tablet detection)
        max_touch_points: components.touchSupport?.value?.maxTouchPoints || navigator.maxTouchPoints,

        // GPU information (stable hardware identifier)
        gpu_vendor: this.extractGPUVendor(components),
        gpu_renderer: this.extractGPURenderer(components),

        // Canvas fingerprint (hardware-based rendering signature)
        canvas_fingerprint: this.hashValue(components.canvas?.value),

        // WebGL fingerprint
        webgl_fingerprint: this.hashValue(components.webglBasics?.value),

        // Audio fingerprint
        audio_fingerprint: this.hashValue(components.audio?.value),

        // Additional useful data
        fonts: components.fonts?.value?.length || 0,
        plugins_count: components.plugins?.value?.length || 0,
        languages: components.languages?.value || navigator.languages,
        vendor: components.vendor?.value || navigator.vendor,

        // The main FingerprintJS visitor ID (unique identifier)
        visitor_id: result.visitorId,

        // Confidence score from FingerprintJS
        confidence_score: result.confidence?.score || 0
      };

      // Cache the result for this session
      this.cachedData = hardwareData;

      return hardwareData;
    } catch (error) {
      console.error('Error collecting hardware fingerprint:', error);
      return this.getBasicFallbackData();
    }
  }

  /**
   * Get just the visitor ID (faster, less comprehensive)
   * Useful for quick device identification
   *
   * @returns Promise<string> Unique visitor ID
   */
  async getVisitorId(): Promise<string> {
    try {
      const fp = await this.fpPromise;
      const result = await fp.get();
      return result.visitorId;
    } catch (error) {
      console.error('Error getting visitor ID:', error);
      return null;
    }
  }

  /**
   * Get comprehensive device fingerprint combining visitorId and hardwareData
   * This provides both the FingerprintJS visitorId and hardware characteristics
   * for more reliable device matching, especially when visitorId changes after cache clear
   *
   * Optimized to avoid regenerating hardwareData if visitorId hasn't changed
   *
   * @returns Promise<any> Object containing visitorId and hardwareData
   */
  async getDeviceFingerprint(): Promise<any> {
    try {
      // Get current visitorId from FingerprintJS
      const currentVisitorId = await this.getVisitorId();

      // Check if we have a cached fingerprint
      const cached = this.getCachedDeviceFingerprint();

      // If we have cached fingerprint and visitorId matches, return cached (no regeneration needed)
      if (cached && cached.visitorId === currentVisitorId && cached.hardwareFingerprint) {
        return cached;
      }

      // VisitorId changed or no cache - regenerate hardware data
      const hardwareData = await this.collectHardwareData();

      const fingerprint = {
        visitorId: currentVisitorId,
        hardwareData: hardwareData,
        // Create a composite fingerprint hash from stable hardware characteristics
        // This can be used for matching even if visitorId changes
        hardwareFingerprint: this.generateHardwareFingerprint(hardwareData)
      };

      // Cache the fingerprint in memory and localStorage for persistence
      this.cachedFingerprint = fingerprint;
      this.saveFingerprintToStorage(fingerprint);

      return fingerprint;
    } catch (error) {
      console.error('Error getting device fingerprint:', error);
      const fallback = {
        visitorId: null,
        hardwareData: this.getBasicFallbackData(),
        hardwareFingerprint: null
      };
      this.cachedFingerprint = fallback;
      return fallback;
    }
  }

  /**
   * Get cached device fingerprint synchronously
   * Used by HTTP interceptor to add fingerprint header to requests
   * Returns cached fingerprint from memory or localStorage
   *
   * @returns any Cached device fingerprint or null
   */
  getCachedDeviceFingerprint(): any {
    // Return from memory cache if available
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }
    // Otherwise try to load from localStorage
    this.loadCachedFingerprint();
    return this.cachedFingerprint;
  }

  /**
   * Get device fingerprint string for HTTP header
   * Returns visitorId (the full FingerprintJS identifier) for consistent device tracking
   *
   * @returns string Device fingerprint string for header
   */
  getDeviceFingerprintHeader(): string | null {
    const fingerprint = this.getCachedDeviceFingerprint();
    if (!fingerprint) {
      return null;
    }
    // Use visitorId as the primary device identifier (full 32-character hash)
    return fingerprint.visitorId || null;
  }

  /**
   * Get visitorId from cached fingerprint or fallback to localStorage DEVICE_ID_KEY
   * This maintains backward compatibility with existing code that uses DEVICE_ID_KEY
   *
   * @returns string VisitorId or null
   */
  getVisitorIdFromCache(): string | null {
    const fingerprint = this.getCachedDeviceFingerprint();
    if (fingerprint && fingerprint.visitorId) {
      return fingerprint.visitorId;
    }
    // Fallback to localStorage DEVICE_ID_KEY for backward compatibility
    try {
      return localStorage.getItem('deviceId');
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate a stable hardware fingerprint hash from key hardware characteristics
   * This remains consistent even when visitorId changes after cache clear
   * Uses the most stable hardware attributes for device identification
   *
   * @param hardwareData Hardware data object
   * @returns string Hash of stable hardware characteristics
   */
  private generateHardwareFingerprint(hardwareData: any): string {
    if (!hardwareData) {
      return null;
    }

    // Use the most stable hardware characteristics for fingerprinting
    const stableAttributes = {
      screen_width: hardwareData.screen_width,
      screen_height: hardwareData.screen_height,
      screen_color_depth: hardwareData.screen_color_depth,
      screen_pixel_ratio: hardwareData.screen_pixel_ratio,
      hardware_concurrency: hardwareData.hardware_concurrency,
      device_memory: hardwareData.device_memory,
      platform: hardwareData.platform,
      gpu_vendor: hardwareData.gpu_vendor,
      gpu_renderer: hardwareData.gpu_renderer,
      canvas_fingerprint: hardwareData.canvas_fingerprint,
      webgl_fingerprint: hardwareData.webgl_fingerprint,
      audio_fingerprint: hardwareData.audio_fingerprint,
      timezone: hardwareData.timezone
    };

    // Generate a hash from these stable attributes
    return this.hashValue(stableAttributes);
  }

  /**
   * Clear cached fingerprint data
   * Call this if you want to force re-collection
   */
  clearCache(): void {
    this.cachedData = null;
    this.cachedFingerprint = null;
    try {
      localStorage.removeItem(DEVICE_FINGERPRINT_KEY);
    } catch (error) {
      console.error('Error clearing fingerprint from localStorage:', error);
    }
  }

  /**
   * Extract GPU vendor from components
   */
  private extractGPUVendor(components: any): string | null {
    if (components.vendorFlavors?.value) {
      return components.vendorFlavors.value[0] || null;
    }

    // Try canvas vendor
    if (components.canvas?.value?.vendor) {
      return components.canvas.value.vendor;
    }

    return null;
  }

  /**
   * Extract GPU renderer from components
   */
  private extractGPURenderer(components: any): string | null {
    if (components.vendorFlavors?.value) {
      return components.vendorFlavors.value[1] || null;
    }

    // Try WebGL renderer
    if (components.webglBasics?.value?.renderer) {
      return components.webglBasics.value.renderer;
    }

    return null;
  }

  /**
   * Simple hash function for component values
   */
  private hashValue(value: any): string | null {
    if (!value) return null;

    const str = JSON.stringify(value);
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Fallback data if FingerprintJS fails
   * Returns minimal but useful information
   */
  private getBasicFallbackData(): any {
    return {
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      screen_color_depth: window.screen.colorDepth,
      screen_pixel_ratio: window.devicePixelRatio || 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezone_offset: new Date().getTimezoneOffset(),
      hardware_concurrency: navigator.hardwareConcurrency,
      platform: navigator.platform,
      languages: navigator.languages,
      visitor_id: null,
      confidence_score: 0
    };
  }
}

